/*
 * Studio Rivello theme behaviour. Ported from the Next.js site (lib/visitor.ts, lib/affiliate.ts).
 * - Routes every Amazon button to the visitor's own store, with that store's tag.
 * - Publishes an `affiliate_click` custom event (Shopify Customer Events → GA4 / TikTok).
 * - Product browser (room / category / type / style chips, sort, search), row arrows,
 *   "More details", gallery dots, sticky Amazon bar, click-to-load TikTok.
 */
(() => {
  "use strict";

  /* ---------- Store routing ---------- */

  const DOMAINS = {
    us: "amazon.com",
    uk: "amazon.co.uk",
    de: "amazon.de",
    fr: "amazon.fr",
    it: "amazon.it",
    es: "amazon.es",
    nl: "amazon.nl",
  };

  // Next stores to try when a visitor's own store has no tag or no link. EU stores ship across the EU.
  const FALLBACKS = {
    us: [],
    uk: [],
    de: ["nl", "fr", "it", "es"],
    nl: ["de", "fr", "it", "es"],
    fr: ["de", "it", "es", "nl"],
    it: ["de", "fr", "es", "nl"],
    es: ["de", "fr", "it", "nl"],
  };

  const STORE_BY_TIMEZONE = {
    "Europe/London": "uk", "Europe/Dublin": "uk", "Europe/Belfast": "uk", "Europe/Jersey": "uk",
    "Europe/Guernsey": "uk", "Europe/Isle_of_Man": "uk", "Europe/Gibraltar": "uk",
    "Europe/Amsterdam": "nl",
    "Europe/Berlin": "de", "Europe/Busingen": "de", "Europe/Vienna": "de",
    "Europe/Paris": "fr", "Europe/Luxembourg": "fr", "Europe/Monaco": "fr",
    "Europe/Rome": "it", "Europe/Vatican": "it", "Europe/San_Marino": "it",
    "Europe/Madrid": "es", "Atlantic/Canary": "es", "Africa/Ceuta": "es",
    "Europe/Lisbon": "es", "Atlantic/Madeira": "es", "Atlantic/Azores": "es",
  };

  const tags = (window.Rivello && window.Rivello.tags) || {};
  const OVERRIDE_KEY = "sr_store";

  // For testing: ?store=de acts like a visitor of that store for this session (?store=auto clears it).
  function storeOverride() {
    try {
      const param = new URLSearchParams(location.search).get("store");
      if (param === "auto") sessionStorage.removeItem(OVERRIDE_KEY);
      else if (param && DOMAINS[param]) sessionStorage.setItem(OVERRIDE_KEY, param);
      const stored = sessionStorage.getItem(OVERRIDE_KEY);
      return stored && DOMAINS[stored] ? stored : null;
    } catch {
      return null;
    }
  }

  function detectMarketplace() {
    const override = storeOverride();
    if (override) return override;
    let zone = "";
    try {
      zone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    } catch {}
    const language = (navigator.language || "").toLowerCase();
    if (zone === "Europe/Brussels") return language.startsWith("fr") ? "fr" : "nl";
    if (zone === "Europe/Zurich") return language.startsWith("fr") ? "fr" : language.startsWith("it") ? "it" : "de";
    if (STORE_BY_TIMEZONE[zone]) return STORE_BY_TIMEZONE[zone];
    if (zone.startsWith("Europe/")) return "de";
    return "us";
  }

  function parseUrl(value) {
    try {
      return new URL(value);
    } catch {
      return null;
    }
  }

  function isStoreHost(url, marketplace) {
    const domain = DOMAINS[marketplace];
    return url.hostname === domain || url.hostname.endsWith("." + domain);
  }

  function linkInStore(link, marketplace) {
    if (marketplace === "us") return parseUrl(link.amazonUrl);
    const value = String(link.marketplaceUrls[marketplace] || "").trim();
    if (!value) return null;
    if (/^[A-Z0-9]{10}$/i.test(value)) {
      return new URL("https://www." + DOMAINS[marketplace] + "/dp/" + value.toUpperCase());
    }
    const url = parseUrl(value);
    return url && isStoreHost(url, marketplace) ? url : null; // wrong-store URLs are ignored
  }

  function buildAmazonUrl(link, locale) {
    let chosen = { url: parseUrl(link.amazonUrl), marketplace: "us" };
    for (const marketplace of [locale, ...(FALLBACKS[locale] || [])]) {
      if (!tags[marketplace]) continue;
      const url = linkInStore(link, marketplace);
      if (url) {
        chosen = { url, marketplace };
        break;
      }
    }
    if (!chosen.url) return { href: link.amazonUrl, marketplace: "us" };
    const tag = tags[chosen.marketplace];
    // Short links (amzn.to) already carry a tag and can't take another one.
    if (tag && isStoreHost(chosen.url, chosen.marketplace)) chosen.url.searchParams.set("tag", tag);
    return { href: chosen.url.toString(), marketplace: chosen.marketplace };
  }

  function routeAmazonLinks() {
    const locale = detectMarketplace();
    document.querySelectorAll("a[data-amazon]").forEach((a) => {
      let marketplaceUrls = {};
      try {
        marketplaceUrls = JSON.parse(a.dataset.marketplaceUrls || "null") || {};
      } catch {}
      const { href, marketplace } = buildAmazonUrl({ amazonUrl: a.dataset.amazonUrl, marketplaceUrls }, locale);
      a.href = href;
      a.dataset.marketplace = marketplace;
    });
  }

  /* ---------- affiliate_click ---------- */

  // Subscribe to it in a custom pixel (Settings → Customer events); Shopify applies the visitor's consent.
  // On collection pages it also reports where the click came from, so clicks per session can be
  // split by device, grid position, sort, filters and button text.
  function deviceClass() {
    if (matchMedia("(min-width: 64rem)").matches) return "desktop";
    if (matchMedia("(min-width: 48rem)").matches) return "tablet";
    return "mobile";
  }

  let clickedThisPage = false;

  document.addEventListener("click", (event) => {
    const a = event.target.closest && event.target.closest("a[data-amazon]");
    if (!a) return;
    const browser = a.closest("[data-browser]");
    const item = a.closest("[data-item]");
    const cta = a.dataset.cta === "split" ? document.documentElement.dataset.cta || "view" : a.dataset.cta || "view";
    const payload = {
      product: a.dataset.product,
      category: a.dataset.category,
      placement: a.dataset.placement,
      marketplace: a.dataset.marketplace,
      cta,
      device: deviceClass(),
      first_on_page: !clickedThisPage,
    };
    clickedThisPage = true;
    if (browser) {
      payload.collection = browser.dataset.collection || "";
      payload.sort = browser.dataset.sort || "";
      payload.filtered = browser.dataset.filtered === "true";
      if (item && item.dataset.position) payload.position = Number(item.dataset.position);
    }
    try {
      if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) {
        Shopify.analytics.publish("affiliate_click", payload);
      }
    } catch {}
  });

  /* ---------- Product browser: chips, filter panel, sort, search ---------- */

  // Filters live in the URL (?room=…&cat=…&type=…&tag=…&sort=newest) so a filtered view can be shared.
  // Counts follow the other active filters; a chip that would show nothing is disabled.
  const CHIP_ON = ["border-bright", "bg-bright", "font-medium", "text-ink"];
  const CHIP_OFF = ["border-line", "text-muted", "enabled:hover:border-muted", "enabled:hover:text-bright"];
  const PILL =
    "inline-flex min-h-10 items-center gap-1.5 rounded-full border border-line bg-panel pr-3 pl-3.5 text-sm text-bright transition-colors hover:border-muted";
  const PILL_X =
    '<svg aria-hidden="true" viewBox="0 0 12 12" class="size-3 text-muted" fill="none" stroke="currentColor" stroke-width="1.75"><path d="m3 3 6 6M9 3l-6 6"/></svg>';

  const pieces = (n) => n + (n === 1 ? " piece" : " pieces");

  function initBrowser(root) {
    const groups = [...root.querySelectorAll("[data-filter-group]")].map((row) => ({
      key: row.dataset.filterGroup,
      row,
      chips: [...row.querySelectorAll("[data-chip]")],
      inPanel: Boolean(row.closest("[data-filter-panel]")),
    }));
    const list = root.querySelector("[data-list]");
    const items = [...root.querySelectorAll("[data-item]")].map((li) => ({
      li,
      room: li.dataset.room || "",
      cats: (li.dataset.cats || "").split(",").filter(Boolean),
      type: li.dataset.type || "",
      tags: (li.dataset.tags || "").split(","),
      text: li.dataset.text || "",
      order: Number(li.dataset.order) || 0,
      created: Number(li.dataset.created) || 0,
    }));
    const search = root.querySelector("[data-search]");
    const count = root.querySelector("[data-count]");
    const showResults = root.querySelector("[data-show-results]");
    const empty = root.querySelector("[data-empty]");
    const activeList = root.querySelector("[data-active]");
    const panelBadge = root.querySelector("[data-panel-count]");
    const sortInputs = [...root.querySelectorAll('input[name="sort"]')];
    const clears = [...root.querySelectorAll("[data-clear]")];
    const results = root.querySelector("[data-results]");
    const sortDefault = root.dataset.sortDefault || "newest";
    const active = {};
    let sort = sortDefault;

    // Start from the URL, ignoring values that don't match a chip or a sort option.
    const params = new URLSearchParams(location.search);
    groups.forEach(({ key, chips }) => {
      const value = params.get(key);
      active[key] = value && chips.some((c) => c.dataset.value === value) ? value : "";
    });
    if (sortInputs.some((input) => input.value === params.get("sort"))) sort = params.get("sort");

    function matches(item, filters, term) {
      if (filters.room && item.room !== filters.room) return false;
      if (filters.cat && !item.cats.includes(filters.cat)) return false;
      if (filters.type && item.type !== filters.type) return false;
      if (filters.tag && !item.tags.includes(filters.tag)) return false;
      return !term || item.text.includes(term);
    }

    // Reorders the list itself (not just how it looks), so keyboard and screen reader order match.
    function applySort() {
      if (!list) return;
      const sorted = [...items].sort((a, b) =>
        sort === "newest" && sortDefault !== "newest" ? b.created - a.created || a.order - b.order : a.order - b.order,
      );
      sorted.forEach((item) => list.appendChild(item.li));
      sortInputs.forEach((input) => (input.checked = input.value === sort));
    }

    function chipLabel(key, value) {
      const group = groups.find((g) => g.key === key);
      const chip = group && group.chips.find((c) => c.dataset.value === value);
      return chip ? chip.dataset.label || chip.textContent.trim() : value;
    }

    function pill(label, onRemove) {
      const li = document.createElement("li");
      const button = document.createElement("button");
      button.type = "button";
      button.className = PILL;
      button.setAttribute("aria-label", "Remove filter: " + label);
      button.innerHTML = PILL_X;
      button.prepend(label);
      button.addEventListener("click", () => {
        onRemove();
        syncUrl();
        render();
        revealResults();
      });
      li.append(button);
      return li;
    }

    function render() {
      const term = (search ? search.value : "").trim().toLowerCase();
      let visible = 0;
      items.forEach((item) => {
        const show = matches(item, active, term);
        item.li.hidden = !show;
        if (show) visible++;
      });

      // Grid position of each visible piece, for the affiliate_click event.
      let position = 0;
      if (list) [...list.children].forEach((li) => (li.dataset.position = li.hidden ? "" : String(++position)));

      groups.forEach(({ key, row, chips }) => {
        chips.forEach((chip) => {
          const value = chip.dataset.value;
          const on = active[key] === value;
          chip.setAttribute("aria-pressed", String(on));
          chip.classList.remove(...(on ? CHIP_OFF : CHIP_ON));
          chip.classList.add(...(on ? CHIP_ON : CHIP_OFF));
          const badge = chip.querySelector("[data-chip-count]");
          // "All": how many the other filters leave; any other chip: how many it would show.
          const n = items.filter((item) => matches(item, { ...active, [key]: value }, term)).length;
          if (value) chip.disabled = n === 0 && !on;
          if (badge) {
            badge.textContent = n;
            badge.className = on ? "text-ink/60" : "text-muted/70";
          }
        });
        keepVisible(row);
      });

      if (count) count.textContent = pieces(visible);
      if (showResults) showResults.textContent = "Show " + pieces(visible);
      if (empty) empty.hidden = visible !== 0;

      const filtered = Object.values(active).some(Boolean) || term !== "";
      clears.forEach((button) => {
        if (!empty || !empty.contains(button)) button.hidden = !(filtered || sort !== sortDefault);
      });

      if (activeList) {
        const pills = [];
        groups.forEach(({ key }) => {
          if (active[key]) pills.push(pill(chipLabel(key, active[key]), () => (active[key] = "")));
        });
        if (term) {
          pills.push(
            pill("“" + search.value.trim() + "”", () => {
              search.value = "";
            }),
          );
        }
        activeList.replaceChildren(...pills);
      }

      if (panelBadge) {
        const inPanel = groups.filter((g) => g.inPanel && active[g.key]).length + (sort !== sortDefault ? 1 : 0);
        panelBadge.textContent = inPanel;
        panelBadge.hidden = inPanel === 0;
      }

      root.dataset.sort = sort;
      root.dataset.filtered = String(filtered);
    }

    // After a filter change deep in the grid, go back to the top of the results (below the toolbar).
    function revealResults() {
      if (!results || results.getBoundingClientRect().top >= 0) return;
      const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
      results.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
    }

    function syncUrl() {
      const url = new URL(location.href);
      groups.forEach(({ key }) => {
        if (active[key]) url.searchParams.set(key, active[key]);
        else url.searchParams.delete(key);
      });
      if (sortInputs.length && sort !== sortDefault) url.searchParams.set("sort", sort);
      else url.searchParams.delete("sort");
      history.replaceState(history.state, "", url);
    }

    groups.forEach(({ key, chips }) =>
      chips.forEach((chip) =>
        chip.addEventListener("click", () => {
          const value = chip.dataset.value;
          active[key] = value && active[key] === value ? "" : value;
          syncUrl();
          render();
          revealResults();
        }),
      ),
    );
    sortInputs.forEach((input) =>
      input.addEventListener("change", () => {
        sort = input.value;
        applySort();
        syncUrl();
        render();
        revealResults();
      }),
    );
    if (search) search.addEventListener("input", render);
    clears.forEach((button) =>
      button.addEventListener("click", () => {
        groups.forEach(({ key }) => (active[key] = ""));
        if (search) search.value = "";
        if (sort !== sortDefault) {
          sort = sortDefault;
          applySort();
        }
        syncUrl();
        render();
        revealResults();
      }),
    );

    if (sort !== sortDefault) applySort();
    render();
  }

  // Keep the active chip in view in a swipeable row, e.g. when opened from a shared link.
  function keepVisible(row) {
    const chip = row.querySelector('[aria-pressed="true"]');
    if (!chip || row.scrollWidth <= row.clientWidth) return;
    const left = chip.offsetLeft - row.offsetLeft;
    const right = left + chip.offsetWidth;
    if (left < row.scrollLeft || right > row.scrollLeft + row.clientWidth) row.scrollTo({ left: left - 20 });
  }

  /* ---------- Product rows: arrow buttons (a mouse can't swipe) ---------- */

  function initScrollRow(root) {
    const track = root.querySelector("[data-scroll-track]");
    const prev = root.querySelector("[data-scroll-prev]");
    const next = root.querySelector("[data-scroll-next]");
    if (!track || !prev || !next) return;

    // Hidden, not removed, so the other arrow doesn't jump; inert keeps it out of the tab order.
    const show = (button, visible) => {
      button.inert = !visible;
      button.classList.toggle("opacity-0", !visible);
    };
    const update = () => {
      show(prev, track.scrollLeft > 1);
      show(next, track.scrollLeft + track.clientWidth < track.scrollWidth - 1);
    };
    const scroll = (direction) => {
      const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
      track.scrollBy({ left: direction * track.clientWidth * 0.8, behavior: reduce ? "auto" : "smooth" });
    };

    prev.addEventListener("click", () => scroll(-1));
    next.addEventListener("click", () => scroll(1));
    track.addEventListener("scroll", update, { passive: true });
    if ("ResizeObserver" in window) new ResizeObserver(update).observe(track);
    update();
  }

  /* ---------- "More details": long bullet descriptions show their first point ---------- */

  function initCollapsible(box) {
    const points = box.querySelectorAll("li");
    if (points.length < 3) return;
    const hiddenCount = points.length - 1;
    const button = document.createElement("button");
    button.type = "button";
    button.className =
      "mt-3 inline-flex min-h-11 items-center gap-2 text-sm text-bright underline decoration-line underline-offset-[6px] hover:decoration-bright";
    const label = () => (box.classList.contains("is-collapsed") ? "More details (" + hiddenCount + ")" : "Fewer details");
    button.setAttribute("aria-expanded", "false");
    box.classList.add("is-collapsed");
    button.textContent = label();
    button.addEventListener("click", () => {
      const collapsed = box.classList.toggle("is-collapsed");
      button.setAttribute("aria-expanded", String(!collapsed));
      button.textContent = label();
    });
    box.after(button);
  }

  /* ---------- Gallery dots ---------- */

  function initGallery(root) {
    const track = root.querySelector("[data-gallery-track]");
    const dots = [...root.querySelectorAll("[data-gallery-dot]")];
    if (!track || dots.length < 2) return;
    const setActive = (index) => dots.forEach((dot, i) => dot.setAttribute("aria-current", String(i === index)));
    track.addEventListener("scroll", () => setActive(Math.round(track.scrollLeft / track.clientWidth)), { passive: true });
    dots.forEach((dot, i) =>
      dot.addEventListener("click", () => {
        const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
        track.scrollTo({ left: i * track.clientWidth, behavior: reduce ? "auto" : "smooth" });
      }),
    );
  }

  /* ---------- Sticky Amazon bar ---------- */

  function initStickyCta(bar) {
    const target = document.getElementById(bar.dataset.stickyCta);
    const footer = document.querySelector("footer");
    if (!target || !("IntersectionObserver" in window)) return;
    const inView = { cta: true, footer: false };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === target) inView.cta = entry.isIntersecting;
        else inView.footer = entry.isIntersecting;
      });
      const visible = !inView.cta && !inView.footer;
      bar.inert = !visible;
      bar.classList.toggle("translate-y-full", !visible);
      bar.classList.toggle("translate-y-0", visible);
    });
    observer.observe(target);
    if (footer) observer.observe(footer);
  }

  /* ---------- TikTok, click to load ---------- */

  function initTikTok(box) {
    const button = box.querySelector("button");
    if (!button) return;
    button.addEventListener("click", () => {
      const iframe = document.createElement("iframe");
      iframe.src = "https://www.tiktok.com/player/v1/" + encodeURIComponent(box.dataset.tiktok) + "?autoplay=1&rel=0&description=0&music_info=0";
      iframe.title = box.dataset.tiktokTitle || "TikTok video";
      iframe.allow = "autoplay; encrypted-media; fullscreen; picture-in-picture";
      iframe.className = "absolute inset-0 size-full border-0";
      box.replaceChildren(iframe);
    });
  }

  /* ---------- Cookie settings ---------- */

  function initCookieSettings(button) {
    // Only shown when Shopify's cookie banner is enabled (Settings → Customer privacy).
    const banner = window.privacyBanner;
    if (!banner || typeof banner.showPreferences !== "function") return;
    button.hidden = false;
    button.addEventListener("click", () => banner.showPreferences());
  }

  function init() {
    routeAmazonLinks();
    document.querySelectorAll("[data-browser]").forEach(initBrowser);
    document.querySelectorAll("[data-scroll-row]").forEach(initScrollRow);
    document.querySelectorAll("[data-collapsible]").forEach(initCollapsible);
    document.querySelectorAll("[data-gallery]").forEach(initGallery);
    document.querySelectorAll("[data-sticky-cta]").forEach(initStickyCta);
    document.querySelectorAll("[data-tiktok]").forEach(initTikTok);
    // The privacy banner script can load after this one.
    const cookieButton = document.querySelector("[data-cookie-settings]");
    if (cookieButton) {
      initCookieSettings(cookieButton);
      window.addEventListener("load", () => cookieButton.hidden && initCookieSettings(cookieButton));
    }
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
